import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';

const MarkdownWrapper = styled.div`
  code {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
    font-size: 0.8125rem;
    background: ${colors.grey5};
    padding: 0.125rem 0.25rem;
    border-radius: 0.125rem;
  }

  pre {
    background: ${colors.grey5};
    border: solid 1px ${colors.grey10};
    border-radius: 0.25rem;
    padding: 0.75rem;
    overflow-x: auto;
    margin-bottom: 0.5rem;

    code {
      background: none;
      padding: 0;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
  }

  th,
  td {
    border: solid 1px ${colors.grey10};
    padding: 0.375rem 0.5rem;
    text-align: left;
  }

  th {
    background: ${colors.grey5};
    font-weight: ${weights.bold};
  }
`;

const ToolBlock = styled.div`
  border: 1px solid ${colors.grey10};
  border-radius: 0.5rem;
  display: inline-block;
`;

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;

  &:hover {
    background: ${colors.grey5};
  }
`;

const ToolStatusIcon = styled(Icon)`
  font-size: 0.75rem;
  color: ${({ $failed }) => ($failed ? colors.danger : colors.success)};
`;

const ToolHeaderChevron = styled.span`
  font-size: 0.625rem;
  color: ${colors.grey40};
  width: 0.75rem;
`;

const ToolHeaderText = styled.span`
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const ToolBody = styled.div`
  position: relative;
  padding: 0 0.75rem 0.75rem 0.75rem;
`;

const ToolConnectingLine = styled.div`
  position: absolute;
  left: 1rem;
  top: 0;
  bottom: 1.5rem;
  width: 1px;
  background: ${colors.grey5};
`;

const ToolStep = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0;
`;

const ToolStepIcon = styled.span`
  width: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  font-size: 0.625rem;

  &.completed {
    color: ${colors.success};
  }
  &.failed {
    color: ${colors.danger};
  }
`;

const ToolStepText = styled.span`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  line-height: 1.4;
`;

const MoreRowsTd = styled.td`
  text-align: center;
  color: ${colors.grey50};
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;

  th,
  td {
    border: solid 1px ${colors.grey10};
    padding: 0.375rem 0.5rem;
    text-align: left;
  }

  th {
    background: ${colors.grey5};
    font-weight: ${weights.bold};
    font-size: 0.75rem;
    color: ${colors.grey55};
  }

  td {
    font-size: 0.75rem;
  }
`;

const ChartMeta = styled.div`
  padding: 0.75rem;
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const ChartLabel = styled.span`
  font-weight: ${weights.bold};
  color: ${colors.black};
  margin-right: 0.5rem;
`;

function ToolsPart({ tools }) {
  const [expanded, setExpanded] = React.useState(true);

  const completedCount = tools.filter((t) => t.status === 'completed').length;
  const failedCount = tools.filter((t) => t.status === 'failed').length;

  let headerText = `${completedCount} step${completedCount !== 1 ? 's' : ''} completed`;
  if (failedCount > 0) headerText += `, ${failedCount} failed`;

  if (tools.length === 0) return null;

  return (
    <ToolBlock>
      <ToolHeader onClick={() => setExpanded(!expanded)}>
        <ToolHeaderChevron>
          <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </ToolHeaderChevron>
        <ToolStatusIcon icon={failedCount > 0 ? 'circle-exclamation' : 'circle-check'} $failed={failedCount > 0} />
        <ToolHeaderText>{headerText}</ToolHeaderText>
      </ToolHeader>

      {expanded && (
        <ToolBody>
          <ToolConnectingLine />
          {tools.map((tool) => (
            <ToolStep key={tool.callId}>
              <ToolStepIcon className={tool.status}>
                <Icon icon={tool.status === 'completed' ? 'check' : 'xmark'} />
              </ToolStepIcon>
              <ToolStepText>{tool.statusText}</ToolStepText>
            </ToolStep>
          ))}
        </ToolBody>
      )}
    </ToolBlock>
  );
}

function TextPart({ content }) {
  return (
    <MarkdownWrapper>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </MarkdownWrapper>
  );
}

function TablePart({ data, fields }) {
  if (!data || !fields || fields.length === 0) return null;

  return (
    <DataTable>
      <thead>
        <tr>
          {fields.map((f) => (
            <th key={f.name || f}>{f.label || f.name || f}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.slice(0, 20).map((row, i) => (
          <tr key={i}>
            {fields.map((f) => {
              const key = f.name || f;
              const value = row[key];
              return <td key={key}>{value != null ? String(value) : ''}</td>;
            })}
          </tr>
        ))}
        {data.length > 20 && (
          <tr>
            <MoreRowsTd colSpan={fields.length}>... {data.length - 20} more rows</MoreRowsTd>
          </tr>
        )}
      </tbody>
    </DataTable>
  );
}

function ChartPart({ chartType, dimension, metric, data, fields }) {
  return (
    <ChartMeta>
      <div>
        <ChartLabel>Chart:</ChartLabel>
        {chartType || 'unknown'} — {dimension} by {metric}
      </div>
      {data && fields && <TablePart data={data} fields={fields} />}
    </ChartMeta>
  );
}

export default function MessageContent({ parts }) {
  if (!parts || parts.length === 0) return null;

  return parts.map((part, i) => {
    if (part.type === 'tools') {
      return <ToolsPart key={`tools-${i}`} tools={part.tools || []} />;
    }

    if (part.type === 'text') {
      return <TextPart key={`text-${i}`} content={part.content || ''} />;
    }

    if (part.type === 'table') {
      return <TablePart key={`table-${i}`} data={part.data} fields={part.fields} />;
    }

    if (part.type === 'chart') {
      return (
        <ChartPart
          key={`chart-${i}`}
          chartType={part.chartType}
          dimension={part.dimension}
          metric={part.metric}
          data={part.data}
          fields={part.fields}
        />
      );
    }

    return null;
  });
}
